import React from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Heart } from 'lucide-react';
import { Badge } from './ui/badge';

const categoryImages = {
  restaurants: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80',
  retail: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=80',
  services: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=80',
  professional: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
  beauty: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800&q=80',
  churches: 'https://images.unsplash.com/photo-1438032005730-c779502df39b?w=800&q=80',
  nonprofits: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800&q=80',
  foundations: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&q=80',
  charities: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&q=80',
  organizations: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=800&q=80',
  photography: 'https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=800&q=80',
  videography: 'https://images.unsplash.com/photo-1579566346927-c68383817a25?w=800&q=80',
  graphicdesign: 'https://images.unsplash.com/photo-1626785774573-4b799315345d?w=800&q=80',
  museums: 'https://images.unsplash.com/photo-1554907984-15263bfd63bd?w=800&q=80',
  filmproduction: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=800&q=80',
  recordingcompany: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80',
  adventures: 'https://images.unsplash.com/photo-1527631746610-bca00a040d60?w=800&q=80',
  cruises: 'https://images.unsplash.com/photo-1548574505-5e239809ee19?w=800&q=80',
  vacationrentals: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=800&q=80',
  travelagencies: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80',
};

const getCategoryImage = (category) => {
  const key = category?.toLowerCase().replace(/[^a-z]/g, '').replace('wellness', '');
  return categoryImages[key] || categoryImages.services;
};

export const BusinessCard = ({ business, featured = false }) => {
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star key={i} className="w-4 h-4 fill-legacy-gold text-legacy-gold" />
        );
      } else if (i === fullStars && hasHalf) {
        stars.push(
          <Star key={i} className="w-4 h-4 fill-legacy-gold/50 text-legacy-gold" />
        );
      } else {
        stars.push(
          <Star key={i} className="w-4 h-4 text-stone-300" />
        );
      }
    }
    return stars;
  };

  const imageUrl = business.image_url || getCategoryImage(business.category);

  return (
    <Link
      to={`/business/${business.business_id}`}
      className="business-card block group"
      data-testid={`business-card-${business.business_id}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={imageUrl}
          alt={business.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        
        {/* Featured Badge */}
        {(featured || business.is_featured) && (
          <Badge className="featured-badge">
            Featured
          </Badge>
        )}

        {/* Save Button */}
        <button
          className="absolute top-4 right-4 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-colors"
          onClick={(e) => {
            e.preventDefault();
            // TODO: Implement save functionality
          }}
          data-testid={`save-business-${business.business_id}`}
        >
          <Heart className="w-4 h-4 text-stone-600" />
        </button>

        {/* Category Badge */}
        <div className="absolute bottom-4 left-4">
          <Badge variant="secondary" className="bg-white/90 backdrop-blur text-emerald-deep">
            {business.category}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-display text-xl font-semibold text-stone-900 group-hover:text-emerald-deep transition-colors mb-2">
          {business.name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-0.5">
            {renderStars(business.average_rating)}
          </div>
          <span className="text-sm text-stone-600">
            {business.average_rating.toFixed(1)} ({business.review_count} reviews)
          </span>
        </div>

        {/* Location */}
        <div className="flex items-center gap-1.5 text-stone-500 text-sm">
          <MapPin className="w-4 h-4" />
          <span>
            {business.city}
            {business.state && `, ${business.state}`}
            {business.country && business.country !== 'United States' && ` • ${business.country}`}
          </span>
        </div>

        {/* Description */}
        <p className="mt-3 text-stone-600 text-sm line-clamp-2">
          {business.description}
        </p>
      </div>
    </Link>
  );
};

export default BusinessCard;
